import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectCPMFTrackingForecastLocationId,
  selectCPMFTrackingForecastMembers,
  selectCPMFTrackingForecastModelId,
  selectCPMFTrackingForecastMultiLocationIds,
  selectCPMFTrackingForecastSubpage,
  selectCPMFTrackingForecastTimeFrequency,
  selectCPMFTrackingForecastVariable,
} from 'redux/reducers/meteorologyFieldsTrackingForecastControlPanelReducer';
import { useLazyGetTrackingForecastDetailedViewDatesQuery } from 'services/api/Meteorology/Fields/TrackingForecast/DetailedView';
import { SUBPAGE_SUBMENU_OPTIONS } from 'types/Meteorology/Fields/TrackingForecast/DetailedView';

export default function useForecastDateOptions() {
  const subpage = useSelector(selectCPMFTrackingForecastSubpage);
  const submenu = SUBPAGE_SUBMENU_OPTIONS[subpage];
  const timeFrequency = useSelector(selectCPMFTrackingForecastTimeFrequency);
  const locationId = useSelector(selectCPMFTrackingForecastLocationId);
  const locationIds = useSelector(selectCPMFTrackingForecastMultiLocationIds);
  const members = useSelector(selectCPMFTrackingForecastMembers);
  const modelId = useSelector(selectCPMFTrackingForecastModelId);
  const variable = useSelector(selectCPMFTrackingForecastVariable);

  const [trigger, {
    data, isFetching,
  }] = useLazyGetTrackingForecastDetailedViewDatesQuery();

  const singleModelFields = useMemo(() => (
    Boolean(timeFrequency && modelId && variable)
  ), [timeFrequency, modelId, variable]);
  const singleLocationFields = useMemo(() => (
    Boolean(timeFrequency && locationId)
  ), [timeFrequency, locationId]);
  const multiLocationFields = useMemo(() => (
    Boolean(singleModelFields && locationIds.length > 0)
  ), [singleModelFields, locationIds]);
  const multiMemberFields = useMemo(() => (
    Boolean(singleModelFields && singleLocationFields && members.length > 0)
  ), [singleModelFields, singleLocationFields, members]);
  const multiModelFields = useMemo(() => (
    Boolean(timeFrequency && locationId)
  ), [timeFrequency, locationId]);
  const multiRoundFields = useMemo(() => (
    Boolean(singleModelFields && singleLocationFields)
  ), [singleModelFields, singleLocationFields]);

  const dates = useMemo(() => {
    if (!data) return [];

    switch (submenu) {
      case 'multi-locais': return multiLocationFields ? data : [];
      case 'multi-membros': return multiMemberFields ? data : [];
      case 'multi-modelos': return multiModelFields ? data : [];
      case 'multi-rodadas': return multiRoundFields ? data : [];
      default: return [];
    }
  }, [data, submenu, multiLocationFields, multiMemberFields, multiModelFields, multiRoundFields]);

  useEffect(() => {
    switch (submenu) {
      case 'multi-locais':
        if (multiLocationFields) {
          trigger({
            submenu,
            timeFrequency,
            modelId,
            variable,
            locationIds,
          });
        }
        break;
      case 'multi-membros':
        if (multiMemberFields) {
          trigger({
            submenu,
            locationId,
            timeFrequency,
            modelId,
            members,
            variable,
          });
        }
        break;
      case 'multi-modelos':
        if (multiModelFields) {
          trigger({
            submenu,
            locationId,
            timeFrequency,
          });
        }
        break;
      case 'multi-rodadas':
        if (multiRoundFields) {
          trigger({
            submenu,
            locationId,
            timeFrequency,
            modelId,
            variable,
          });
        }
        break;
      default: break;
    }
  }, [
    submenu, multiLocationFields, multiMemberFields, multiModelFields, multiRoundFields,
    locationId, timeFrequency, modelId, variable, locationIds, members, trigger,
  ]);

  return { dates, isFetching };
}
